<template>
  <div
    class="calc-main repayment sliders"
    data-name="Repayment calculator"
  >
    <div
      v-if="!isSubmitted"
      id="sliders-page"
    >
      <section class="calc-main__container">
        <form
          class="form_wrapper"
          action
        >
          <div class="block-header"><h3>Your existing first mortgage information</h3></div>
          <div class="row">
            <div class="col-12 col-md-6 input-fields">
              <div
                id="input"
                class="input__wrapper"
              >
                <div
                  id="monthly-payment-input-group"
                  class="form-group"
                >
                  <label
                    for="monthly-payment-input-field"
                  >Monthly Payment Amount</label>
                  <span
                    class="tooltipContent"
                    data-toggle="tooltip"
                    data-placement="right"
                    data-html="true"
                    title="<p>The total amount you are supposed to pay your mortgage company each month. If you are unsure of this amount, refer to a recent mortgage statement or call your mortgage company to find out.</p>"
                  >
                    <i class="fa fa-info-circle fa-1x" />
                  </span>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">$</div>
                    </div>
                    <CurrencyComponent
                      id="monthlyPayment"
                      v-model.number="$v.monthlyPayment.$model"
                      class="form-control"
                      aria-label="Monthly Payment Amount"
                      :class="{
                        'is-invalid':
                          $v.monthlyPayment.$error ||
                          $v.monthlyPayment.$invalid
                      }"
                      @input="updateTotalAmount('monthlyPayment')"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">&nbsp;</div>
                    </div>
                    <input
                      id="monthly-payment-input-range"
                      v-model.number="monthlyPayment"
                      type="range"
                      aria-label="Monthly Payment Amount Range"
                      class="custom-range"
                      min="1"
                      max="100000"
                      @input="updateTotalAmount('monthly-payment-input-range')"
                      @change="sendDataLayer()"
                    >
                    <div class="invalid-feedback">
                      <span
                        v-if="
                          !$v.monthlyPayment.between ||
                            !$v.monthlyPayment.required
                        "
                      >{{ errorMsgPre }} ${{
                        Number(
                          $v.monthlyPayment.$params.between.min
                        ).toLocaleString()
                      }}
                        and ${{
                          Number(
                            $v.monthlyPayment.$params.between.max
                          ).toLocaleString()
                        }}</span>
                    </div>
                  </div>
                </div>
                <!-- Payments past due section -->
                <div
                  id="number-of-payment-past-due-input-group"
                  class="form-group"
                >
                  <label for="payment-past-due-input-field">Number of Payments Past Due</label>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">&nbsp;</div>
                    </div>
                    <input
                      id="noOfPaymentPastDue"
                      v-model.number="$v.noOfPaymentPastDue.$model"
                      type="number"
                      aria-label="Number of Payments Past Due"
                      class="form-control"
                      :class="{
                        'is-invalid':
                          $v.noOfPaymentPastDue.$error ||
                          $v.noOfPaymentPastDue.$invalid
                      }"
                      @input="updateTotalAmount('noOfPaymentPastDue'); "
                      @blur="sendDataLayerOnBlur"
                    >
                    <div class="input-group-append">
                      <div class="input-group-text" />
                    </div>
                    <input
                      id="down-payment-input-range"
                      v-model.number="noOfPaymentPastDue"
                      type="range"
                      aria-label="Number Of Payments Past Due Range"
                      class="custom-range"
                      min="1"
                      max="25"
                      @input="updateTotalAmount('down-payment-input-range');"
                      @change="sendDataLayer()"
                    >
                    <div class="invalid-feedback">
                      <span
                        v-if="
                          !$v.noOfPaymentPastDue.between ||
                            !$v.noOfPaymentPastDue.required
                        "
                      >{{ errorMsgPre }}
                        {{
                          Number(
                            $v.noOfPaymentPastDue.$params.between.min
                          ).toLocaleString()
                        }} and
                        {{
                          Number(
                            $v.noOfPaymentPastDue.$params.between.max
                          ).toLocaleString()
                        }}</span>
                    </div>
                  </div>
                </div>

                <!-- Total Amount Past Due -->
                <div
                  id="total-amount-past-due-input-group"
                  class="form-group"
                >
                  <label
                    for="total-amount-past-due-input-field"
                  >Or, Total Dollar Amount Past Due</label>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">$</div>
                    </div>
                    <CurrencyComponent
                      v-model.number="$v.totalAmountPastDue.$model"
                      class="form-control"
                      aria-label="Or, Total Dollar Amount Past Due"
                      :class="{
                        'is-invalid':
                          $v.totalAmountPastDue.$error ||
                          $v.totalAmountPastDue.$invalid
                      }"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">&nbsp;</div>
                    </div>
                    <input
                      id="total-amount-past-due-input-range"
                      v-model.number="totalAmountPastDue"
                      type="range"
                      aria-label="Or, Total Dollar Amount Past Due Range"
                      class="custom-range"
                      min="1"
                      max="500000"
                      @change="sendDataLayer()"
                    >
                    <div class="invalid-feedback">
                      <span
                        v-if="
                          !$v.totalAmountPastDue.between
                        "
                      >{{ errorMsgPre }} ${{
                        Number(
                          $v.totalAmountPastDue.$params.between.min
                        ).toLocaleString()
                      }}
                        and ${{
                          Number(
                            $v.totalAmountPastDue.$params.between.max
                          ).toLocaleString()
                        }}</span>
                    </div>
                  </div>
                </div>

                <div
                  id="repayment-plan"
                  class="form-group"
                >
                  <label
                    style="margin-bottom: 0px;"
                    for="total-amount-past-due-input-field"
                  >Number of Months for Repayment Plan</label>
                  <span class="desc">(Usually 3-9 months. This example uses a 6 month repayment period.)</span>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <span class="input-text">6 months</span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                id="button-section"
                class="col"
              >

                <div class="row">
                  <div
                    id="form-mod-group"
                    class="form-group col col-md-6"
                  >
                    <span
                      class="text-button button-white fm-btn"
                      onclick="dataLayer.push({
                          'event': 'calculator-cta-link',
                          'calculatorName': 'Repayment calculator',
                          'calculatorCta': 'Reset',
                        });"
                      @click="reset"
                    >Reset
                    </span>
                  </div>
                </div>

              </div>
            </div>
            <div
              id="result"
              class="col-12 col-md-6 chart"
            >
              <div class="sticky">
                <div
                  id="graph-content"
                  class="tab-content"
                >
                  <div
                    id="graph-info"
                    class="tab-pane fade show active"
                    role="tabpanel"
                    aria-labelledby="graph-info"
                  >
                    <div class="card-main graph">
                      <div class="card-bottom">
                        <div class="row graph-content">
                          <GraphComponent
                            :values="graphDataValues"
                            :center-text="graphDataCenterText"
                          />
                        </div>
                        <div class="row graph-text">
                          <label class="col-8">Existing monthly first mortgage payment</label>
                          <span class="col-4 magenta">{{ currencyFormat(Math.round((monthlyMortgageInsurance))) }}</span>
                        </div>
                        <div class="row graph-text">
                          <label class="col-8">Additional amount paid each month</label>
                          <span
                            class="col-4 blue"
                          >{{ currencyFormat(Math.round(additonalAmountPaid)) }}</span>
                        </div>
                        <div class="row graph-last-text">
                          <label class="col-8">New temporary mortgage payment</label>
                          <span
                            class="col-4"
                            style="color: #212121; font-size: 20px; font-weight: 700; text-align: right"
                          >{{ currencyFormat(Math.round(newMortgageAmountPayment)) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>
        </form>
      </section>
    </div>
  </div>
</template>


<script>
import jQuery from 'jquery';
import { required, between } from 'vuelidate/lib/validators';
import GraphComponent from '../components/GraphComponent.vue';
import CurrencyComponent from '../components/Currency.vue';

const $ = jQuery;
const currency = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  style: 'currency',
  currency: 'USD',
});

export default {
  name: 'RepaymentCalculator',
  components: {
    GraphComponent,
    CurrencyComponent,
  },
  data() {
    return this.init();
  },
  validations: {
    monthlyPayment: {
      required,
      between: between(1, 100000),
    },
    noOfPaymentPastDue: {
      required,
      between: between(1, 25),
    },
    totalAmountPastDue: {
      between: between(1, 500000),
    },
  },
  computed: {
    mortgageLoanAmount() {
      return this.monthlyPayment - this.noOfPaymentPastDue;
    },
    monthlyMortgageInsurance() {
      return this.monthlyPayment;
    },
    additonalAmountPaid() {
      return this.totalAmountPastDue / 6;
    },
    newMortgageAmountPayment() {
      return this.monthlyMortgageInsurance + this.additonalAmountPaid;
    },
    graphDataValues() {
      const valOne = Object();
      valOne.value = this.newMortgageAmountPayment;
      valOne.color = '#911A58';

      const valTwo = Object();
      valTwo.value = this.additonalAmountPaid;
      valTwo.color = '#0C77BA';

      // eslint-disable-next-line
      const values = Array();
      values[0] = valOne;
      values[1] = valTwo;
      return values;
    },
    graphDataCenterText() {
      // eslint-disable-next-line
      const centerText = Array();
      centerText[0] = 'New temporary';
      centerText[1] = 'mortgage payment';
      centerText[2] = this.newMortgageAmountPayment;

      return centerText;
    },
  },
  mounted() {
    $('.sliders').tooltip({ selector: '[data-toggle="tooltip"]' });
    // eslint-disable-next-line func-names
    $(document).on('touchstart', '.tooltipContent', function() {
      $(this).tooltip('show');
    });
    // eslint-disable-next-line func-names
    $(document).on('touchend', '.tooltip', function() {
      $(this).tooltip('hide');
    });
  },
  methods: {
    init() {
      return {
        isSubmitted: false,
        monthlyPayment: 3000,
        noOfPaymentPastDue: 2,
        totalAmountPastDue: 6000,
        simpleView: true,
        closingCostMultiplier: 0.025,
        errorMsgPre: 'Please enter a valid amount between',
      };
    },
    reset() {
      this.monthlyPayment = this.init().monthlyPayment;
      this.noOfPaymentPastDue = this.init().noOfPaymentPastDue;
      this.totalAmountPastDue = this.init().totalAmountPastDue;
    },
    updateTotalAmount(field) {
      if (this.noOfPaymentPastDue != null) {
        this.totalAmountPastDue = this.monthlyPayment * this.noOfPaymentPastDue;
      }
      this.$nextTick(() => {
        document.getElementById(field).focus();
      });
    },
    currencyFormat(n) {
      return currency.format(n);
    },
    formatNumber(num) {
      return Number(num).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    sendDataLayer() {
      window.dataLayer.push({
        event: 'calc-interaction',
        calculatorName: 'Repayment calculator',
      });
    },
    sendDataLayerInput(field) {
      const $element = document.getElementById(field);
      if ($element != null) {
        $element.addEventListener('blur', event => {
          console.log(event.target.value);
          this.sendDataLayer();
        });
      }
    },
    sendDataLayerOnBlur(evt) {
      console.log(`value=${evt.target.value}`);
      window.dataLayer.push({
        event: 'calc-interaction',
        calculatorName: 'Repayment calculator',
      });
    },
  },
};
</script>
<style lang="sass">
</style>
